import { useState, useCallback, useMemo } from 'react'
import { X, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useChatContext } from '@/core/providers/ChatProvider'
import { useDraggable } from '@/core/hooks/useDraggable'
import { ConversacionesList } from './ConversacionesList'
import { ChatWindow } from './ChatWindow'
import { cn } from '@/lib/utils'
import type { ConversacionConPreview } from '@/core/types/chat'

// Tamaño del botón flotante (debe coincidir con w-12 h-12 = 48px)
const BUBBLE_SIZE = { width: 48, height: 48 }
const BUBBLE_STORAGE_KEY = 'brainhub:chat-bubble-position'

// Mobile: bottom sheet style
// Desktop: fixed panel bottom-right

export function ChatPanel() {
  const isMobile = useIsMobile()
  const { chatOpen, setChatOpen, abrirConversacion, conversacionActiva, cerrarConversacion, unreadCount } = useChatContext()
  const [selectedConv, setSelectedConv] = useState<ConversacionConPreview | null>(null)

  // Posición inicial de la burbuja flotante (esquina inferior derecha).
  // En mobile, subimos un poco más (bottom-20) para no tapar la nav inferior.
  const defaultBubblePosition = useMemo(() => {
    if (typeof window === 'undefined') {
      return { x: 0, y: 0 }
    }
    const rightMargin = 16
    const bottomMargin = isMobile ? 80 : 16
    return {
      x: window.innerWidth - BUBBLE_SIZE.width - rightMargin,
      y: window.innerHeight - BUBBLE_SIZE.height - bottomMargin,
    }
    // Solo recalculamos al cambiar isMobile (raro) — la posición real viaja vía localStorage.
  }, [isMobile])

  const {
    position: bubblePosition,
    isDragging: bubbleIsDragging,
    handlers: bubbleHandlers,
    wasDragged: bubbleWasDragged,
    resetWasDragged: resetBubbleDragged,
  } = useDraggable({
    storageKey: BUBBLE_STORAGE_KEY,
    defaultPosition: defaultBubblePosition,
    size: BUBBLE_SIZE,
    pinX: 'right', // se arrastra solo en vertical; X siempre pegada al borde derecho
  })

  const handleSelectConversacion = useCallback((conv: ConversacionConPreview) => {
    setSelectedConv(conv)
    abrirConversacion(conv.id)
  }, [abrirConversacion])

  const handleBack = useCallback(() => {
    setSelectedConv(null)
    cerrarConversacion()
  }, [cerrarConversacion])

  const handleClose = useCallback(() => {
    setChatOpen(false)
    setSelectedConv(null)
    cerrarConversacion()
  }, [setChatOpen, cerrarConversacion])

  // Click en la burbuja: abrir panel, a menos que venga de un drag.
  const handleBubbleClick = useCallback(() => {
    if (bubbleWasDragged) {
      resetBubbleDragged()
      return
    }
    setChatOpen(true)
  }, [bubbleWasDragged, resetBubbleDragged, setChatOpen])

  if (!chatOpen) {
    // Floating button with unread badge — draggable.
    // Usamos <button> nativo en vez de <Button> porque necesitamos control
    // fino sobre pointerdown y estilos inline de posición.
    return (
      <button
        type="button"
        onPointerDown={bubbleHandlers.onPointerDown}
        onClick={handleBubbleClick}
        aria-label={unreadCount > 0 ? `Abrir chat (${unreadCount} no leídos)` : 'Abrir chat'}
        style={{
          position: 'fixed',
          left: `${bubblePosition.x}px`,
          top: `${bubblePosition.y}px`,
          width: `${BUBBLE_SIZE.width}px`,
          height: `${BUBBLE_SIZE.height}px`,
          touchAction: 'none',
          cursor: bubbleIsDragging ? 'grabbing' : 'grab',
        }}
        className={cn(
          'z-50 rounded-full shadow-lg bg-primary text-primary-foreground',
          'flex items-center justify-center hover:shadow-xl transition-shadow',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          bubbleIsDragging && 'scale-105 shadow-2xl'
        )}
      >
        <MessageSquare className="h-5 w-5 pointer-events-none" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 pointer-events-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    )
  }

  // Panel content
  const panelContent = (
    <div className="flex flex-col h-full bg-background">
      {/* Close button (only when showing list, ChatWindow has its own back button) */}
      {!selectedConv && (
        <div className="flex items-center justify-end px-2 pt-2 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {selectedConv && conversacionActiva ? (
        <ChatWindow
          conversacion={selectedConv}
          onBack={handleBack}
        />
      ) : (
        <ConversacionesList onSelectConversacion={handleSelectConversacion} />
      )}
    </div>
  )

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        {panelContent}
      </div>
    )
  }

  // Desktop: fixed panel
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 h-[600px] max-h-[calc(100vh-2rem)] rounded-xl border bg-background shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
      {panelContent}
    </div>
  )
}
